import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../../constants/api';
import { useNotification } from '../../contexts/NotificationContext';

const ASD_FONT = "'abcNormal','Inter','Arial',sans-serif";

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

const formatClp = (n) => `$${Number(n || 0).toLocaleString('es-CL')}`;

const STATUS_META = {
  authorized: { label: 'Activa', bg: '#ECFDF5', color: '#059669' },
  paused: { label: 'Pausada', bg: '#FFFBEB', color: '#B45309' },
  cancelled: { label: 'Cancelada', bg: '#FEF2F2', color: '#B91C1C' },
  pending: { label: 'Pendiente', bg: '#F1F5F9', color: '#475569' },
};

const PAYMENT_STATUS_META = {
  approved: { label: 'Aprobado', color: '#059669' },
  rejected: { label: 'Rechazado', color: '#B91C1C' },
  refunded: { label: 'Reembolsado', color: '#B45309' },
  charged_back: { label: 'Contracargo', color: '#B91C1C' },
};

const ACTION_LABELS = {
  cancel: 'Canceló la suscripción',
  pause: 'Pausó la suscripción',
  resume: 'Reanudó la suscripción',
  resync: 'Re-verificó con MercadoPago',
  extend: 'Extendió el acceso',
  refund: 'Reembolsó un pago',
};

// Mirrors AdminSubscriptionsTab's EditPlanModal structurally (overlay +
// centered white card), prefixed `asd-` (the next letter in the a[tab]t-
// convention after ast-/apt-) for any scoped CSS this file might need —
// currently none beyond Tailwind utilities, kept for consistency with the
// sibling admin components.
const SubscriptionDetailModal = ({ user, onClose, onChanged }) => {
  const { confirm, toast } = useNotification();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [extendDate, setExtendDate] = useState('');
  const [refundPaymentId, setRefundPaymentId] = useState('');
  const [refundAmount, setRefundAmount] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/subscriptions/${user.id}/detail`);
      setDetail(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cargar el detalle de la suscripción');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user.id]);

  const sub = detail?.subscription;
  const status = sub?.status;
  const statusMeta = STATUS_META[status] || { label: status || 'Sin suscripción', bg: '#F1F5F9', color: '#475569' };

  const runAction = async (action, requestFn, successMsg) => {
    setBusy(true);
    try {
      await requestFn();
      toast.success(successMsg);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `No se pudo completar: ${action}`);
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    const ok = await confirm(
      'Se cancelará el cobro recurrente en MercadoPago ahora mismo. Si el usuario ya pagó el período actual, conserva acceso Pro hasta que termine; si no, pasa a Free de inmediato.',
      { confirmLabel: 'Sí, cancelar' },
    );
    if (!ok) return;
    await runAction('cancel', () =>
      axios.post(`${API}/admin/subscriptions/${user.id}/cancel`, { reason: cancelReason || undefined }),
      'Suscripción cancelada.',
    );
    setCancelReason('');
  };

  const handlePause = async () => {
    const ok = await confirm('Se pausará el cobro en MercadoPago y el usuario pierde el acceso Pro de inmediato (se puede reanudar después sin pedirle la tarjeta de nuevo).', { confirmLabel: 'Sí, pausar' });
    if (!ok) return;
    await runAction('pause', () => axios.post(`${API}/admin/subscriptions/${user.id}/pause`), 'Suscripción pausada.');
  };

  const handleResume = async () => {
    await runAction('resume', () => axios.post(`${API}/admin/subscriptions/${user.id}/resume`), 'Suscripción reanudada.');
  };

  const handleResync = async () => {
    await runAction('resync', () => axios.post(`${API}/admin/subscriptions/${user.id}/resync`), 'Estado re-verificado con MercadoPago.');
  };

  const handleExtend = async () => {
    if (!extendDate) return;
    await runAction(
      'extend',
      () => axios.post(`${API}/admin/subscriptions/${user.id}/extend`, { new_access_until: new Date(extendDate).toISOString() }),
      'Acceso extendido.',
    );
    setExtendDate('');
  };

  const handleRefund = async () => {
    if (!refundPaymentId) return;
    const ok = await confirm(
      refundAmount
        ? `Se reembolsará $${Number(refundAmount).toLocaleString('es-CL')} CLP de este pago en MercadoPago.`
        : 'Se reembolsará el monto total de este pago en MercadoPago. Si la suscripción sigue activa, el acceso Pro termina de inmediato.',
      { confirmLabel: 'Sí, reembolsar' },
    );
    if (!ok) return;
    await runAction(
      'refund',
      () => axios.post(`${API}/admin/subscriptions/${user.id}/refund`, {
        payment_id: refundPaymentId,
        amount: refundAmount ? Number(refundAmount) : undefined,
      }),
      'Pago reembolsado.',
    );
    setRefundPaymentId('');
    setRefundAmount('');
  };

  const canPause = status === 'authorized';
  const canCancel = status === 'authorized';
  const canResume = status === 'paused';
  const canExtend = (status === 'cancelled' || status === 'paused') && !!sub?.access_until;
  const approvedPayments = (detail?.payments || []).filter((p) => p.status === 'approved');

  return (
    <div
      data-testid="admin-subscription-detail-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6"
      style={{ background: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden flex flex-col"
        style={{ maxHeight: '88vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-gray-100 flex items-start justify-between">
          <div>
            <h3 style={{ fontFamily: ASD_FONT, fontSize: 19, fontWeight: 600, color: '#1E293B' }}>Gestionar suscripción</h3>
            <p className="text-sm text-[#64748B] mt-0.5">{user.email}</p>
          </div>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#475569] text-xl leading-none px-1">×</button>
        </div>

        <div className="p-6 overflow-y-auto space-y-5">
          {loading ? (
            <div className="text-center py-10 text-[#64748B] text-sm">Cargando…</div>
          ) : !sub ? (
            <div className="text-center py-10 text-[#64748B] text-sm">Este usuario no tiene ninguna suscripción registrada con MercadoPago.</div>
          ) : (
            <>
              {/* Status header */}
              <div className="rounded-xl p-4" style={{ background: '#F8FAFC' }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-semibold" style={{ background: statusMeta.bg, color: statusMeta.color }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusMeta.color }} />
                    {statusMeta.label}
                  </span>
                  <span className="text-xs text-[#94A3B8]">{sub.billing_period === 'annual' ? 'Facturación anual' : 'Facturación mensual'}</span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-xs text-[#94A3B8]">Monto</div>
                    <div className="font-medium text-[#1E293B]">{formatClp(sub.amount_clp)} CLP</div>
                  </div>
                  <div>
                    <div className="text-xs text-[#94A3B8]">Próximo cobro</div>
                    <div className="font-medium text-[#1E293B]">{formatDateTime(sub.next_payment_date)}</div>
                  </div>
                  {sub.access_until && (
                    <div className="col-span-2">
                      <div className="text-xs text-[#94A3B8]">Acceso Pro garantizado hasta</div>
                      <div className="font-medium text-[#1E293B]">{formatDateTime(sub.access_until)}</div>
                    </div>
                  )}
                  {sub.last_payment_status && (
                    <div className="col-span-2">
                      <div className="text-xs text-[#94A3B8]">Último estado de pago</div>
                      <div className="font-medium" style={{ color: PAYMENT_STATUS_META[sub.last_payment_status]?.color || '#1E293B' }}>
                        {PAYMENT_STATUS_META[sub.last_payment_status]?.label || sub.last_payment_status}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-2.5">
                <div className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Acciones</div>
                <div className="flex flex-wrap gap-2">
                  <button disabled={busy} onClick={handleResync} className="px-3 py-1.5 text-xs font-medium rounded-lg transition disabled:opacity-40" style={{ background: '#F1F5F9', color: '#475569' }}>
                    Re-verificar con MercadoPago
                  </button>
                  {canPause && (
                    <button disabled={busy} onClick={handlePause} className="px-3 py-1.5 text-xs font-medium rounded-lg transition disabled:opacity-40" style={{ background: '#FFFBEB', color: '#B45309' }}>
                      Pausar
                    </button>
                  )}
                  {canResume && (
                    <button disabled={busy} onClick={handleResume} className="px-3 py-1.5 text-xs font-medium rounded-lg transition disabled:opacity-40" style={{ background: '#ECFDF5', color: '#059669' }}>
                      Reanudar
                    </button>
                  )}
                </div>

                {canCancel && (
                  <div className="flex gap-2 items-center pt-1">
                    <input
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      placeholder="Motivo (opcional)"
                      className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#004AAD]/20"
                    />
                    <button disabled={busy} onClick={handleCancel} className="px-3 py-1.5 text-xs font-medium rounded-lg transition disabled:opacity-40 whitespace-nowrap" style={{ background: '#FEF2F2', color: '#B91C1C' }}>
                      Cancelar suscripción
                    </button>
                  </div>
                )}

                {canExtend && (
                  <div className="flex gap-2 items-center pt-1">
                    <input
                      type="date"
                      value={extendDate}
                      onChange={(e) => setExtendDate(e.target.value)}
                      className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#004AAD]/20"
                    />
                    <button disabled={busy || !extendDate} onClick={handleExtend} className="px-3 py-1.5 text-xs font-medium rounded-lg transition disabled:opacity-40 whitespace-nowrap" style={{ background: 'rgba(0,74,173,0.08)', color: '#004AAD' }}>
                      Extender acceso hasta esta fecha
                    </button>
                  </div>
                )}

                {approvedPayments.length > 0 && (
                  <div className="flex gap-2 items-center pt-1 flex-wrap">
                    <select
                      value={refundPaymentId}
                      onChange={(e) => setRefundPaymentId(e.target.value)}
                      className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#004AAD]/20 bg-white"
                    >
                      <option value="">Reembolsar un pago…</option>
                      {approvedPayments.map((p) => (
                        <option key={p.mp_payment_id} value={p.mp_payment_id}>
                          {formatDateTime(p.created_at)} — {formatClp(p.amount_clp)} CLP
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      value={refundAmount}
                      onChange={(e) => setRefundAmount(e.target.value)}
                      placeholder="Monto parcial (opcional)"
                      className="w-36 px-3 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#004AAD]/20"
                    />
                    <button disabled={busy || !refundPaymentId} onClick={handleRefund} className="px-3 py-1.5 text-xs font-medium rounded-lg transition disabled:opacity-40 whitespace-nowrap" style={{ background: '#FEF2F2', color: '#B91C1C' }}>
                      Reembolsar
                    </button>
                  </div>
                )}
              </div>

              {/* Payment history */}
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-2">Historial de pagos</div>
                {(detail.payments || []).length === 0 ? (
                  <p className="text-xs text-[#94A3B8]">Sin pagos registrados aún.</p>
                ) : (
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {detail.payments.map((p) => {
                      const meta = PAYMENT_STATUS_META[p.status] || { label: p.status, color: '#64748B' };
                      return (
                        <div key={p.id} className="flex items-center justify-between text-xs px-2.5 py-1.5 rounded-lg" style={{ background: '#FAFAFA' }}>
                          <span className="text-[#64748B]">{formatDateTime(p.created_at)}</span>
                          <span className="font-medium text-[#1E293B]">{formatClp(p.amount_clp)} CLP</span>
                          <span className="font-medium" style={{ color: meta.color }}>{meta.label}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Admin action timeline */}
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-2">Historial de acciones de administrador</div>
                {(detail.actions || []).length === 0 ? (
                  <p className="text-xs text-[#94A3B8]">Sin acciones registradas aún.</p>
                ) : (
                  <div className="space-y-1.5 max-h-32 overflow-y-auto">
                    {detail.actions.map((a) => (
                      <div key={a.id} className="text-xs">
                        <span className="text-[#1E293B] font-medium">{ACTION_LABELS[a.action] || a.action}</span>
                        <span className="text-[#94A3B8]"> · {a.admin_email} · {formatDateTime(a.created_at)}</span>
                        {a.detail && <div className="text-[#94A3B8] mt-0.5">{a.detail}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionDetailModal;
